import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useNDACompliance } from '@/hooks/useNDACompliance';
import { useTenantStore } from '@/hooks/useTenantStore';
import { Loader2 } from 'lucide-react';

interface NDAAgreementGuardProps {
  children: ReactNode;
}

export function NDAAgreementGuard({ children }: NDAAgreementGuardProps) {
  const { tenantId } = useTenantStore();
  const location = useLocation();
  const { needsAcceptance, loading, activeNDA } = useNDACompliance(tenantId || undefined);

  // Don't block if we're already on the NDA acceptance page
  const isOnNDAPage = location.pathname === '/nda-acceptance';

  useEffect(() => {
    // Debug logging
    if (import.meta.env.DEV) {
      console.log('NDA Guard:', {
        tenantId,
        needsAcceptance,
        loading,
        hasActiveNDA: !!activeNDA,
        currentPath: location.pathname,
      });
    }
  }, [tenantId, needsAcceptance, loading, activeNDA, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Checking access requirements...</p>
        </div>
      </div>
    );
  }

  // If NDA is required and user hasn't accepted, redirect to acceptance page
  if (needsAcceptance && !isOnNDAPage && activeNDA) {
    return (
      <Navigate
        to="/nda-acceptance"
        state={{
          from: location.pathname,
          companyId: tenantId,
          ndaId: activeNDA.id,
        }}
        replace
      />
    );
  }

  return <>{children}</>;
}
